import { NgIf } from '@angular/common';
import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { CobaltAvatarComponent } from '@cae/cobalt-angular/components/avatar';
import {
  CobaltGlobalHeaderComponent,
  CobaltUserMenuComponent,
  CobaltUserMenuProfile,
  CobaltUserMenuTriggerDirective
} from '@cae/cobalt-angular/components/global-header';
import {
  cobaltIconCogOutline,
  CobaltIconComponent,
  cobaltIconLogOutOutline,
  CobaltIconRegistry,
  cobaltIconUserAvatarOutline
} from '@cae/cobalt-angular/components/icon';
import { CobaltLinkComponent } from '@cae/cobalt-angular/components/link';
import { CobaltModalService } from '@cae/cobalt-angular/components/modal';
import { CobaltSideNavigationComponent } from '@cae/cobalt-angular/components/side-navigation';
import { AnalyticPilotEventTypes } from '@core/enums/analytic/analytic-pilot-event-types.enum';
import { AnalyticPilotLogoutProperties } from '@core/models/analytic/analytic-pilot-logout-properties.model';
import { AnalyticPilotPopupPageLoadedProperties } from '@core/models/analytic/analytic-pilot-popup-page-loaded-properties.model';
import { AnalyticPilotPopupPageUnloadedProperties } from '@core/models/analytic/analytic-pilot-popup-page-unloaded-properties.model';
import { AnalyticService } from '@core/services/analytic/analytic.service';
import { AppSettingsService } from '@core/services/app-settings/app-settings.service';
import { MonitorService } from '@core/services/monitor/monitor.service';
import { UserService } from '@core/services/user/user.service';
import { User } from '@core/states/user/user-state.model';
import { Contact } from '@training/models/contact.model';
import { GraphApiContact } from '@training/models/graph-api-contact.model';
import { TrainingEvent } from '@training/models/training/training-event.model';
import { GraphApiContactDataService } from '@training/services/graph-api-contact/graph-api-contact-data.service';
import { TrainingEventsDataService } from '@training/services/training-events/training-events-data.service';
import { TrainingEventsService } from '@training/services/training-events/training-events.service';
import { TrainingEventsStateModel } from '@training/states/training-events/training-events.state';
import { AuthB2CService } from 'common-web-core';
import { lastValueFrom, Observable, of } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';

import { HeaderService } from './header.service';
import { ContactDialogComponent } from '../contact-dialog/contact-dialog.component';
import { LoadingCircularPanelService } from '../loading-circular-panel/loading-circular-panel.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.less'],
  standalone: true,
  imports: [
    NgIf,
    CobaltAvatarComponent,
    CobaltGlobalHeaderComponent,
    CobaltIconComponent,
    CobaltLinkComponent,
    CobaltUserMenuComponent,
    CobaltUserMenuTriggerDirective
  ]
})
export class HeaderComponent implements OnInit {
  @Input() sideNavigationRef: CobaltSideNavigationComponent;

  hasHelp: boolean;
  hasEula: boolean;
  eulaUrl: string;
  buildNumber: string;
  contact: Contact;
  trainingEvent: TrainingEvent;
  isSMSNotificationSettingEnabled?: boolean;
  public userProfile: CobaltUserMenuProfile;

  private _destroyRef = inject(DestroyRef);

  constructor(
    private _analyticService: AnalyticService,
    private _appSettingsService: AppSettingsService,
    private _authB2cService: AuthB2CService,
    private _cobaltIconRrgistry: CobaltIconRegistry,
    private _graphApiContactService: GraphApiContactDataService,
    private _headerService: HeaderService,
    private _loadingPanelService: LoadingCircularPanelService,
    private _modalService: CobaltModalService,
    private _monitorService: MonitorService,
    private _router: Router,
    private _trainingEventsDataService: TrainingEventsDataService,
    private _trainingEventService: TrainingEventsService,
    private _userService: UserService
  ) {
    this._cobaltIconRrgistry.registerIcons([
      cobaltIconUserAvatarOutline,
      cobaltIconLogOutOutline,
      cobaltIconCogOutline
    ]);

    this.hasHelp =
      this._appSettingsService.configuration.headerSettings.hasHelp;
    this.hasEula =
      this._appSettingsService.configuration.headerSettings.hasEula;
    this.eulaUrl =
      this._appSettingsService.configuration.headerSettings.eulaUrl;
  }

  public ngOnInit(): void {
    this._userService.getUser().subscribe((user: User) => {
      this.userProfile = new CobaltUserMenuProfile(
        user.email,
        user.givenName,
        user.lastName
      );
    });

    this.buildNumber = this._appSettingsService.configuration.buildNumber;

    // region Contact Card logic stuff
    this._trainingEventService.trainingEventsState$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap(async (trainingEventsState: TrainingEventsStateModel) => {
          if (!!trainingEventsState && !!trainingEventsState.trainingEvents) {
            const eventsWithContacts = this.filterEvents(
              trainingEventsState.trainingEvents
            );

            if (Array.isArray(eventsWithContacts) && !eventsWithContacts.length)
              return;

            // Loop through events to get GraphApi data.
            // If despite both the AccountExecutive info and GraphApi there's still no email and phone number,
            // ...try to get info for the next event.
            for (const event of eventsWithContacts) {
              this.assignContact(event);

              if (
                event.reservationAccountExecutive &&
                event.reservationAccountExecutive.accountExecutiveEmail
              ) {
                await lastValueFrom(
                  this.callGraphApi(
                    event.reservationAccountExecutive.accountExecutiveEmail
                  )
                );
              }

              if (this.verifyIsShowContactPopup()) {
                this.openContactModal();
                break;
              }
            }
          }
        }),
        /* You want to display the contact card only once:
         * the first emission is empty state initialization,
         * second emission will be your data
         */
        take(2)
      )
      .subscribe(
        (response: any) => {
          // Process the response and set showElement based on the data received
          this.isSMSNotificationSettingEnabled =
            response.isSMSNotificationSettingEnabled;
        },
        (error: any) => {
          console.log(
            'Error while going through trainingEventsState$ for contact card: ' +
            error
          );
        }
      );
  }

  headerPanelToggled(isOpened: boolean): void {
    if (isOpened) {
      this._analyticService.post(
        AnalyticPilotEventTypes.PopupPageLoaded,
        new AnalyticPilotPopupPageLoadedProperties(
          this._router.url,
          'Header Panel'
        )
      );
    } else {
      const analyticEvent = new AnalyticPilotPopupPageUnloadedProperties(
        this._router.url,
        'Header Panel'
      );
      this._analyticService.post(
        AnalyticPilotEventTypes.PopupPageUnloaded,
        analyticEvent
      );
    }
  }

  logoutClicked(): void {
    this._analyticService.post(
      AnalyticPilotEventTypes.Logout,
      new AnalyticPilotLogoutProperties()
    );
    localStorage.setItem('cct-web-logout-evt', this.generateTimestamp());
    this._loadingPanelService.toggleLoadingCircularPanelVisibility(true);
    sessionStorage.clear();
    localStorage.clear();
    this._authB2cService.logout();
  }

  generateTimestamp(): string {
    const currentDate = new Date();
    return currentDate.toISOString();
  }

  helpClicked(): void {
    this._headerService.showHelp();
  }

  eulaClicked(): void {
    const eulaWindow = window.open(
      this.eulaUrl,
      '_blank',
      'noopener,noreferrer'
    );
    if (eulaWindow) eulaWindow.opener = null;
  }

  // region Contact Card logic stuff
  verifyIsShowContactPopup(): boolean {
    return !!this.contact && !!this.contact.email && !!this.contact.phoneNumber;
  }

  filterEvents(trainingEvents: TrainingEvent[]): Array<TrainingEvent> {
    const date2WeeksFromNow = new Date();
    date2WeeksFromNow.setDate(date2WeeksFromNow.getDate() + 14);

    const eventsWithContacts1 = trainingEvents.filter(
      (n) =>
        n.orderProgramType == 'BAT' &&
        n.startDate <= date2WeeksFromNow &&
        n.startDate >= new Date() &&
        !n.isContactCardDisplayedWeb &&
        n.reservationAccountExecutive
    );

    // Sort filtered events by date, to start with the most urgent.
    if (eventsWithContacts1.length <= 0) return [];

    eventsWithContacts1.sort(function (a, b) {
      return a.startDate.getTime() - b.startDate.getTime();
    });

    return eventsWithContacts1;
  }

  assignContact(event: TrainingEvent): void {
    this.contact = {
      name: event.reservationAccountExecutive.accountExecutiveName,
      reservationId: event.geminiReservationId,
      phoneNumber:
        event.reservationAccountExecutive.accountExecutivePhoneNumber,
      email: event.reservationAccountExecutive.accountExecutiveEmail,
      profilePicture: 'assets/images/empty-user.svg',
      location: event.trainingLocationDisplayName,
      availabilityEndTime: '--:--',
      availabilityStartTime: '--:--',
      trainingEventDate: event.startDate
    };
  }

  callGraphApi(email: string): Observable<GraphApiContact> {
    return this._graphApiContactService.getGraphApiContactInfo(email).pipe(
      tap((graphApiContact) => {
        this.processGraphResponse(graphApiContact);
      })
    );
  }

  processGraphResponse(graphApiContact): void {
    if (!graphApiContact) return;

    if (graphApiContact.photo) {
      this.contact.profilePicture =
        'data:image/jpg;base64,' + graphApiContact.photo;
    }

    if (graphApiContact.workingHour) {
      this.contact.availabilityStartTime =
        graphApiContact.workingHour.startTime;
      this.contact.availabilityEndTime = graphApiContact.workingHour.endTime;
    }

    if (!this.contact.phoneNumber) {
      if (
        graphApiContact.businessPhones &&
        graphApiContact.businessPhones.length > 0
      ) {
        this.contact.phoneNumber = graphApiContact.businessPhones[0];
      } else if (graphApiContact.mobilePhone) {
        this.contact.phoneNumber = graphApiContact.mobilePhone;
      }
    }
  }

  public openContactModal(): void {
    this.setContactCardIsDisplayed();

    // TODO: This trainingEventService call is only for trainingEvent_Timing in heap. Is it necessary?
    if (this.contact.reservationId) {
      this._trainingEventService.trainingEvents$
        .pipe(
          takeUntilDestroyed(this._destroyRef),
          map((trainingEvents: TrainingEvent[]) => {
            return trainingEvents?.find(
              (x) =>
                x.geminiReservationId == this.contact.reservationId ||
                x.reservationId == this.contact.reservationId
            );
          }, take(1))
        )
        .subscribe((evt) => {
          this.trainingEvent = evt;
        });
    }
    this.logHeap('Web_Contact_Card_Displayed');

    this._modalService.open(ContactDialogComponent, {
      data: { contact: this.contact, user: this.userProfile },
      size: 'medium'
    });
  }

  setContactCardIsDisplayed(): void {
    this._trainingEventsDataService
      .setContactCardIsDisplayed(this.contact.reservationId)
      .pipe(
        catchError((e) => {
          console.log(
            'There was an error when saving the displayed popup status for this event. Error: ',
            e
          );

          return of(null);
        }),
        take(1)
      )
      .subscribe();
  }

  logHeap(source: string): void {
    this._monitorService.logEvent({
      name: source,
      properties: {
        user: this.userProfile,
        trainingEvent_Timing: this.trainingEvent?.timing
      }
    });
  }

  protected onDestroy(): void { }

  settingClicked(): void {
    this._router.navigate(['user/settings/notifications'], {
      state: { isSMSNotificationEnabled: this.isSMSNotificationSettingEnabled }
    });
  }
}
